import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import s from '../tagsBlock.module.css';

export interface IMaterialSelectorProps {
    selectedMaterials: string[];
    onSelectMaterials: (materials: string[]) => void;
    availableMaterials: string[];
    onAddNewMaterial?: (materialName: string) => Promise<void>;
}

export const MaterialSelector: React.FC<IMaterialSelectorProps> = ({
                                                                       selectedMaterials,
                                                                       onSelectMaterials,
                                                                       availableMaterials,
                                                                       onAddNewMaterial
                                                                   }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [localSelectedMaterials, setLocalSelectedMaterials] = useState<string[]>(selectedMaterials);

    const handleMaterialSelect = useCallback((material: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const newSelectedMaterials = localSelectedMaterials.includes(material)
            ? localSelectedMaterials.filter(m => m !== material)
            : [...localSelectedMaterials, material];

        setLocalSelectedMaterials(newSelectedMaterials);
        onSelectMaterials(newSelectedMaterials);
    }, [localSelectedMaterials, onSelectMaterials]);

    const filteredMaterials = availableMaterials.filter(material =>
        material?.toLowerCase()?.includes(searchTerm.toLowerCase())
    );

    const canAddNewMaterial = searchTerm.length > 0 &&
        !availableMaterials.some(material =>
            material.toLowerCase() === searchTerm.toLowerCase()
        );

    const handleAddNewMaterial = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canAddNewMaterial || !onAddNewMaterial) return;

        setIsAdding(true);
        try {
            await onAddNewMaterial(searchTerm);
            const newSelectedMaterials = [...localSelectedMaterials, searchTerm];
            setLocalSelectedMaterials(newSelectedMaterials);
            onSelectMaterials(newSelectedMaterials);
            setSearchTerm('');
        } catch (error) {
            toast.error('Ошибка при добавлении материала');
            setLocalSelectedMaterials(selectedMaterials);
        } finally {
            setIsAdding(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleToggleDropdown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        setLocalSelectedMaterials(selectedMaterials);
    }, [selectedMaterials]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={s.dropdownContainer} ref={wrapperRef}>
            <div
                className={`${s.dropdownButton} ${isOpen ? s.open : ''} ${s.modalDropdown}`}
                onClick={handleToggleDropdown}
            >
                <span className={s.dropdownPlaceholder}>
                    {selectedMaterials.length > 0
                        ? `Выбрано материалов: ${selectedMaterials.length}`
                        : 'Выберите материалы...'
                    }
                </span>
                <span className={s.dropdownArrow}>▼</span>
            </div>

            {isOpen && (
                <div className={`${s.dropdownMenu} ${s.modalDropdownMenu}`} onClick={(e) => e.stopPropagation()}>
                    <div className={s.dropdownHeader}>
                        <span>Выберите материалы</span>
                        <span className={s.selectedCount}>
                            Выбрано: {selectedMaterials.length}
                        </span>
                    </div>

                    <div className={s.searchContainer}>
                        <div className={s.searchWithButton}>
                            <Form.Control
                                type="text"
                                placeholder="Поиск материалов..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onClick={(e) => e.stopPropagation()}
                                className={s.searchInput}
                                autoFocus
                            />
                            <button
                                type="button"
                                className={`${s.addButton} ${canAddNewMaterial ? s.addButtonActive : ''}`}
                                onClick={handleAddNewMaterial}
                                disabled={!canAddNewMaterial || isAdding}
                                title={canAddNewMaterial ? `Добавить "${searchTerm}"` : 'Невозможно добавить материал'}
                            >
                                {isAdding ? (
                                    <div className={s.spinnerSmall}></div>
                                ) : (
                                    '+'
                                )}
                            </button>
                        </div>
                    </div>

                    <div className={s.dropdownOptions}>
                        {filteredMaterials.length > 0 ? (
                            filteredMaterials.map(material => (
                                <div
                                    key={material}
                                    className={`${s.dropdownOption} ${localSelectedMaterials.includes(material) ? s.selected : ''}`}
                                    onClick={(e) => handleMaterialSelect(material, e)}
                                >
                                    <span className={s.optionLabel}>{material}</span>
                                    {localSelectedMaterials.includes(material) && (
                                        <span className={s.checkmark}>✓</span>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className={s.noMaterials}>
                                {searchTerm ? 'Материалы не найдены' : 'Нет доступных материалов'}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};